import React, { useState } from 'react';
import { Container, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import api from '../api'; // Importa l'istanza axios configurata

const MusicaImport = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' or 'danger'
    const [csvColumns, setCsvColumns] = useState([]);
    const [columnMapping, setColumnMapping] = useState({});
    
    const isMobile = useMediaQuery({ maxWidth: 991 });

    const musicaSchemaFields = [
        { key: 'titolo', label: 'Titolo', required: true },
        { key: 'immagine_url', label: 'URL Immagine' },
        { key: 'anno', label: 'Anno' },
        { key: 'genere', label: 'Genere' },
        { key: 'artista', label: 'Artista' },
        { key: 'descrizione', label: 'Descrizione' },
        { key: 'casa_di_produzione', label: 'Casa di Produzione' },
        { key: 'categoria', label: 'Categoria' },
        { key: 'supporto', label: 'Supporto' },
        { key: 'sku', label: 'SKU' },
        { key: 'barcode', label: 'Barcode' },
        { key: 'barcode_ibs', label: 'Barcode IBS' },
        { key: 'prezzo_negozio', label: 'Prezzo Negozio' },
        { key: 'prezzo_ibs', label: 'Prezzo IBS' },
        { key: 'condizione', label: 'Condizione' },
        { key: 'quantita', label: 'Quantità' },
        { key: 'fuori_catalogo', label: 'Fuori Catalogo' },
        { key: 'iva', label: 'IVA' },
        { key: 'costo_acquisto', label: 'Costo Acquisto' },
        { key: 'descrizione_web', label: 'Descrizione Web' },
        { key: 'ubicazione', label: 'Ubicazione' },
        { key: 'fornitore_id', label: 'ID Fornitore' },
    ];

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
        setMessage('');
        setCsvColumns([]);
        setColumnMapping({});

        if (file) {
            const formData = new FormData();
            formData.append('file', file);

            try {
                const response = await api.post('/musica/import/columns', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                const columns = response.data;
                setCsvColumns(columns);

                // Attempt to pre-fill mapping based on exact matches
                const initialMapping = {};
                musicaSchemaFields.forEach(field => {
                    const matchedColumn = columns.find(col => col.toLowerCase() === field.label.toLowerCase() || col.toLowerCase() === field.key.toLowerCase());
                    if (matchedColumn) {
                        initialMapping[field.key] = matchedColumn;
                    }
                });
                setColumnMapping(initialMapping);

            } catch (error) {
                console.error('Errore durante il recupero delle colonne:', error);
                setMessage(error.response?.data?.detail || 'Errore durante il recupero delle colonne del file.');
                setMessageType('danger');
            }
        }
    };

    const handleMappingChange = (schemaField, csvColumn) => {
        setColumnMapping(prev => ({
            ...prev,
            [schemaField]: csvColumn === '' ? null : csvColumn,
        }));
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setMessage('Seleziona un file CSV/Excel da importare.');
            setMessageType('danger');
            return;
        }

        // Validate required fields are mapped
        const requiredFieldsMapped = musicaSchemaFields.filter(field => field.required).every(field => columnMapping[field.key]);
        if (!requiredFieldsMapped) {
            setMessage('Assicurati di mappare tutti i campi obbligatori (es. Titolo).');
            setMessageType('danger');
            return;
        }

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('mapping', JSON.stringify(columnMapping));

        try {
            const response = await api.post('/musica/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setMessage(response.data.message);
            setMessageType('success');
            setSelectedFile(null); // Clear selected file after successful upload
            setCsvColumns([]);
            setColumnMapping({});
        } catch (error) {
            console.error('Errore durante l\'importazione del file:', error);
            setMessage(error.response?.data?.detail || 'Errore durante l\'importazione del file.');
            setMessageType('danger');
        }
    };

    return (
        <Container>
            <h2>Importa Musica da CSV/Excel</h2>
            {message && <Alert variant={messageType}>{message}</Alert>}
            <Form>
                <Form.Group controlId="formFile" className="mb-3">
                    <Form.Label>Seleziona un file CSV o Excel</Form.Label>
                    <Form.Control type="file" accept=".csv, .xls, .xlsx" onChange={handleFileChange} />
                </Form.Group>

                {csvColumns.length > 0 && (
                    <div className="mb-3">
                        <h3>Mappa Colonne</h3>
                        <p>Associa le colonne del tuo file ai campi del database.</p>
                        {musicaSchemaFields.map(field => {
                            const selectedCsvColumn = columnMapping[field.key];
                            const mappedColumns = Object.values(columnMapping).filter(val => val !== null);
                            const availableCsvColumns = csvColumns.filter(col => 
                                !mappedColumns.includes(col) || col === selectedCsvColumn
                            );

                            return (
                                <Row key={field.key} className="mb-2 align-items-center">
                                    <Col sm={4} className="text-sm-end">
                                        <Form.Label className="mb-0">{field.label} {field.required && <span className="text-danger">*</span>}</Form.Label>
                                    </Col>
                                    <Col sm={8}>
                                        <Form.Select
                                            value={selectedCsvColumn || ''}
                                            onChange={(e) => handleMappingChange(field.key, e.target.value)}
                                        >
                                            <option value="">Ignora</option>
                                            {availableCsvColumns.map(col => (
                                                <option key={col} value={col}>{col}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                </Row>
                            );
                        })}
                    </div>
                )}

                <Button variant="primary" onClick={handleUpload} disabled={!selectedFile || csvColumns.length === 0}>
                    Importa
                </Button>
            </Form>
        </Container>
    );
};

export default MusicaImport;
